import { useCallback, useEffect, useMemo, useState } from 'react';

import { PropertyChangeItem } from '../SyncUpdatesModal.types';
import { groupChanges } from '../utils/groupChanges';

interface UseSyncUpdatesModalParams {
  changes: PropertyChangeItem[];
  onSkipSync: () => void;
  onSyncSelected: (selectedProperties: string[]) => void;
}

export const useSyncUpdatesModal = ({ changes, onSkipSync, onSyncSelected }: UseSyncUpdatesModalParams) => {
  const groups = useMemo(() => groupChanges(changes), [changes]);

  const [expandedProperties, setExpandedProperties] = useState<Set<string>>(
    () => new Set((groups[0]?.items ?? []).map((i) => i.propertyName)),
  );
  const [allExpanded, setAllExpanded] = useState(false);

  useEffect(() => {
    setExpandedProperties(new Set((groups[0]?.items ?? []).map((i) => i.propertyName)));
    setAllExpanded(false);
  }, [groups]);

  const [selected, setSelected] = useState<Set<string>>(new Set());

  const allPropertyNames = useMemo(() => changes.map((c) => c.propertyName), [changes]);
  const isAllSelected = selected.size === allPropertyNames.length && allPropertyNames.length > 0;
  const isAllIndeterminate = selected.size > 0 && selected.size < allPropertyNames.length;

  const handleToggleAll = useCallback(() => {
    setSelected(isAllSelected ? new Set() : new Set(allPropertyNames));
  }, [isAllSelected, allPropertyNames]);

  const handleToggleProperty = useCallback((propertyName: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      const item = changes.find((c) => c.propertyName === propertyName);
      const dependents = item?.isRoot && item.groupType
        ? changes.filter((c) => !c.isRoot && c.groupType === item.groupType)
        : [];

      if (next.has(propertyName)) {
        next.delete(propertyName);
        dependents.forEach((c) => next.delete(c.propertyName));
      } else {
        next.add(propertyName);
        dependents.forEach((c) => next.add(c.propertyName));
      }
      return next;
    });
  }, [changes]);

  const handleToggleGroupProperties = useCallback((groupItems: PropertyChangeItem[]) => {
    const groupNames = groupItems.map((i) => i.propertyName);
    setExpandedProperties((prev) => {
      const isAllGroupExpanded = groupNames.every((n) => prev.has(n));
      const next = new Set(prev);
      if (isAllGroupExpanded) {
        groupNames.forEach((n) => next.delete(n));
      } else {
        groupNames.forEach((n) => next.add(n));
      }
      return next;
    });
  }, []);

  const handleToggleExpandAll = useCallback(() => {
    if (allExpanded) {
      setExpandedProperties(new Set());
      setAllExpanded(false);
    } else {
      setExpandedProperties(new Set(allPropertyNames));
      setAllExpanded(true);
    }
  }, [allExpanded, allPropertyNames]);

  const handleFooterAction = useCallback(() => {
    if (selected.size === 0) {
      onSkipSync();
    } else {
      onSyncSelected(Array.from(selected));
    }
  }, [selected, onSkipSync, onSyncSelected]);

  return {
    groups,
    selected,
    expandedProperties,
    allExpanded,
    isAllSelected,
    isAllIndeterminate,
    handleToggleAll,
    handleToggleProperty,
    handleToggleGroupProperties,
    handleToggleExpandAll,
    handleFooterAction,
  };
};
